/**
 * Can an AI model run here at all? Asked before a screen draws an agent's
 * button, so it can say plainly that the agent cannot run instead of offering a
 * button that fails after the click (build contract §2.8, failing late).
 *
 * A model runs when the person has their own key in this browser (Settings →
 * My Connectors), or the deployment has one: the server route that spends it
 * answers GET with which providers it can call (booleans only).
 * Unknown — the check could not be made, or a separate LLM service is set — is
 * `null`. An AI control treats unknown as no model (useAiControl): it is not
 * drawn, and the one no-model line is shown instead.
 */

import { useEffect, useState } from 'react';
import { providerHasApiKey, useMyAiKeys } from '../aiProviderKeys';
import { resolveLlmChatSimpleUrl } from '../envConfig';
import { getSupabaseClient } from '../supabaseClient';

export interface AiProviders {
  anthropic: boolean;
  openai: boolean;
}

let cached: Promise<AiProviders | null> | null = null;

/** Which providers the deployment can call; null when that cannot be read. Asked once per page load. */
export function fetchDeploymentAiProviders(): Promise<AiProviders | null> {
  if (cached) return cached;
  cached = (async () => {
    const url = resolveLlmChatSimpleUrl();
    // Only our own route answers this, and only it may see the sign-in token.
    if (!url.startsWith('/')) return null;
    try {
      const { data } = await getSupabaseClient().auth.getSession();
      const token = data.session?.access_token;
      if (!token) return null;
      const res = await fetch(url, { method: 'GET', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) return null;
      const body = (await res.json()) as { providers?: Partial<AiProviders> };
      if (!body.providers) return null;
      return { anthropic: body.providers.anthropic === true, openai: body.providers.openai === true };
    } catch {
      return null;
    }
  })();
  return cached;
}

/**
 * Can a workspace agent's model call run? The same order callAgentLlm (lib/llmClient)
 * tries: the deployment route — always with an OpenAI model, it swaps others for
 * gpt-4o — then the person's own key for either provider. True / false when known;
 * null when it could not be read.
 */
export function canRunAgentModel(deployment: AiProviders | null): boolean | null {
  if (providerHasApiKey('openai') || providerHasApiKey('anthropic')) return true;
  if (!deployment) return null;
  return deployment.openai;
}

/**
 * {@link canRunAgentModel} with whether the check has finished, so a screen can
 * wait for the answer instead of drawing a control and then taking it away.
 * `checked` is true once the deployment has answered (or could not be read);
 * a saved key answers at once. With `ask` false nothing is fetched (the
 * capability is switched off, so there is nothing to decide) and the result is
 * `{ canRun: null, checked: true }`.
 */
export function useAiModelAvailability(ask = true): { canRun: boolean | null; checked: boolean } {
  // Re-read when the person's saved keys load or change (migration 982).
  useMyAiKeys();
  const [deployment, setDeployment] = useState<AiProviders | null | undefined>(undefined);
  useEffect(() => {
    if (!ask) return;
    let live = true;
    void fetchDeploymentAiProviders().then((p) => live && setDeployment(p));
    return () => {
      live = false;
    };
  }, [ask]);
  if (!ask) return { canRun: null, checked: true };
  const canRun = canRunAgentModel(deployment ?? null);
  return { canRun, checked: canRun === true || deployment !== undefined };
}

/** React form of {@link canRunAgentModel}: null while unknown. */
export function useCanRunAgentModel(): boolean | null {
  return useAiModelAvailability().canRun;
}

/**
 * Whether to draw an AI control (Ask AI, Fix with AI, the ⌘K Ask row, an
 * agent's ask) for a capability that is switched on or off (`enabled`). The one
 * rule every AI control uses, so two surfaces never disagree:
 * - while the check runs: nothing is drawn and no line is shown (no flash);
 * - a model is known to answer (a key in this browser, or the deployment says
 *   yes): `drawable`;
 * - known not to, or the check failed or could not be read: not drawable, and
 *   `needsKey`, so the screen draws the one no-model line
 *   (components/shared/AskAiNeedsKey) instead of a control that fails after the
 *   click (build contract §2.8).
 * With the capability switched off, neither.
 */
export function useAiControl(enabled: boolean): { drawable: boolean; needsKey: boolean } {
  const { canRun, checked } = useAiModelAvailability(enabled);
  if (!enabled || !checked) return { drawable: false, needsKey: false };
  return canRun === true ? { drawable: true, needsKey: false } : { drawable: false, needsKey: true };
}
